import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Form, Input, Select, Row, Col, Space, message, Modal } from "antd";
import { InnerForm, ProductData, PublishSettings } from "./styles";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../../utils/notificationToast";
import Main from "../../../../layout/main/Main";
import COLORS from "../../../../layout/theme/colors/Colors";
import UploadFileImage from "../../../../common/upload-file-with-image/UploadFileImage";
import { CloseCircleOutlined } from "@ant-design/icons";
import { get_clients_name_list } from "../../../../../api/endpoints/clients";
import { create_ia_plan } from "../../../../../api/endpoints/iaPlan";
import PrimaryButton from "../../../../common/antd-buttons/PrimaryButton";
import DefaultButton from "../../../../common/antd-buttons/DefaultButton";

const GUTTER = 20;

const getBase64 = (img, callback) => {
	const reader = new FileReader();
	reader.addEventListener("load", () => callback(reader.result));
	reader.readAsDataURL(img);
};

const states = [
	{
		id: "ACTIVE",
		name: "Activo",
	},
	{
		id: "DELETED",
		name: "Inactivo",
	},
];

export default function CreateIAPlan() {
	const [form] = Form.useForm();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");
	const [clientsForIAPlan, setClientsForIAPlan] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [imageUrl, setImageUrl] = useState();
	const [clients, setClients] = useState(null);
	const tokenPrice = Form.useWatch("tokenPrice", form);

	const value100k = tokenPrice ? Number(tokenPrice) * 100000 : 0;

	const fetch_clients_list = async (term) => {
		const res = await get_clients_name_list(term);

		setClients(
			res.map((r) => ({
				label: r.name,
				value: r.id,
			})),
		);
	};

	useEffect(() => {
		fetch_clients_list();
	}, []);

	const onFinish = async (values) => {
		try {
			setSubmitting(true);

			const postData = {
				title: values?.title,
				tokenPrice: values?.tokenPrice,
				tokenQty: values?.tokenQty,
				planPrice: values?.planPrice,
				clients: clientsForIAPlan,
			};

			const formData = new FormData();
			formData.append(
				"file",
				values.file?.fileList?.slice(-1)[0]?.originFileObj,
			);

			formData.append("data", JSON.stringify(postData));

			const res = await create_ia_plan(formData);
			navigate(`/admin/iaPlans/${res}?uri=${uri}`);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Plan creado con exito",
				"El plan se creo con exito",
			);
		} catch (error) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"Error al crear el plan",
				"Un error ocurrio al intentar crear el plan, contacte con soporte a soporte@onetransfer.com.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleImageChange = (info) => {
		try {
			const isJpgOrPng =
				info.file.type === "image/jpeg" || info.file.type === "image/png";

			if (!isJpgOrPng) {
				message.error("You can only upload JPG/PNG file!");
			}

			const isLt2M = info.file.size / 1024 / 1024 < 2;

			if (!isLt2M) {
				message.error("Image must smaller than 2MB!");
			}

			if (!isJpgOrPng || !isLt2M) return;

			getBase64(
				info.fileList[info.fileList.length - 1].originFileObj,
				(url) => {
					setImageUrl(url);
				},
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handleBack = () => {
		Modal.confirm({
			title: "¿Seguro que quiere volver sin guardar?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content:
				"Si regresa sin guardar se perderán los datos que hayan sido completados",
			okText: "Sí",
			okType: "danger",
			cancelText: "No",
			onOk: () => {
				navigate("/admin/distributors?uri=" + uri);
			},
		});
	};

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/" + uri}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/iaPlans?uri=" + uri}>Planes</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>Crear plan</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>

			<Main.Body>
				<Form
					form={form}
					name="create-plan"
					onFinish={onFinish}
					autoComplete="off"
					requiredMark={false}
					layout="vertical"
				>
					<InnerForm>
						<ProductData>
							<Space
								direction="vertical"
								size="middle"
								style={{ display: "flex" }}
							>
								<Row gutter={GUTTER}>
									<Col span={6}>
										<Form.Item
											name="file"
											label={
												<label
													style={{ color: COLORS.Primary, fontWeight: "600" }}
												>
													Logo del plan
												</label>
											}
											rules={[]}
										>
											<UploadFileImage
												onChange={handleImageChange}
												urlImage={imageUrl}
												imageHeight="100%"
												imageWidth="100%"
											/>
										</Form.Item>
									</Col>
									<Col span={18}>
										<Row gutter={GUTTER}>
											<Col span={12}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Nombre
														</label>
													}
													name="title"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
														{
															pattern: new RegExp(
																/^(?![-.\(\)\s]*$)[-.\(\)a-zA-ZÀ-ÿ0-9\s]*$/,
															),
															message:
																"No se aceptan caracteres especiales. Solo letras y numeros",
														},
													]}
												>
													<Input placeholder="Nombre" />
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													label={
														<div
															style={{
																display: "flex",
																alignItems: "center",
																justifyContent: "space-between",
																gap: 30,
															}}
														>
															<label
																style={{
																	color: COLORS.Primary,
																	fontWeight: "600",
																}}
															>
																Precio Token
															</label>
															<div
																style={{
																	fontSize: 13,
																	color: "#666",
																}}
															>
																(100k tokens:{" "}
																<strong>{value100k.toFixed(2)} USD</strong>)
															</div>
														</div>
													}
													name="tokenPrice"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Input
														type="number"
														placeholder="Precio token"
														min={0}
														step={0.00001}
														style={{ width: "100%" }}
														addonAfter="USD"
													/>
												</Form.Item>{" "}
											</Col>
										</Row>
										<Row gutter={GUTTER}>
											<Col span={12}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Limite Tokens
														</label>
													}
													name="tokenQty"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Input
														type="number"
														placeholder="Limite token"
														min={1}
														style={{ width: "100%" }}
														addonAfter="un."
													/>
												</Form.Item>
											</Col>
											<Col span={12}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Precio Plan
														</label>
													}
													name="planPrice"
													rules={[
														{
															required: true,
															message: "Este campo es obligatorio",
														},
													]}
												>
													<Input
														type="number"
														placeholder="Precio plan"
														min={1}
														style={{ width: "100%" }}
														addonAfter="USD"
													/>
												</Form.Item>
											</Col>
										</Row>
										<Row gutter={GUTTER}>
											<Col span={24}>
												<Form.Item
													label={
														<label
															style={{
																color: COLORS.Primary,
																fontWeight: "600",
															}}
														>
															Clientes
														</label>
													}
													name="pharmacies"
													rules={[]}
												>
													<Select
														showSearch
														mode="multiple"
														placeholder="Buscar cliente"
														optionFilterProp="children"
														onChange={(e) => setClientsForIAPlan(e)}
														value={
															clientsForIAPlan?.length > 0
																? clientsForIAPlan
																: []
														}
														filterOption={(input, option) =>
															(option?.label ?? "")
																.toLowerCase()
																.includes(input.toLowerCase())
														}
														options={clients}
													/>
												</Form.Item>
											</Col>
										</Row>
									</Col>
								</Row>
							</Space>
						</ProductData>
						<PublishSettings>
							<Space
								direction="vertical"
								size="middle"
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Form.Item>
									<PrimaryButton
										htmlType="submit"
										loading={submitting}
										style={{ width: 220 }}
									>
										Guardar
									</PrimaryButton>
								</Form.Item>
								<DefaultButton
									loading={submitting}
									onClick={handleBack}
									style={{
										border: `1px solid ${COLORS.MediumGrey}`,
										width: 220,
									}}
								>
									Volver sin guardar
								</DefaultButton>
							</Space>
						</PublishSettings>
					</InnerForm>
				</Form>
			</Main.Body>
		</Main>
	);
}
